import { BarChart3, CheckCircle, XCircle, HelpCircle, AlertTriangle } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface DecisionStatsData {
  readonly message: string
  readonly stats: {
    readonly total: number
    readonly permit: number
    readonly deny: number
    readonly notApplicable: number
    readonly indeterminate: number
    readonly breakdown?: readonly {
      readonly key: string
      readonly permit: number
      readonly deny: number
      readonly total: number
    }[]
  }
}

export function DecisionStatsCard({ data }: { data: DecisionStatsData }) {
  const { stats } = data
  const total = stats.total ?? 0
  const permit = stats.permit ?? 0
  const deny = stats.deny ?? 0
  const notApplicable = stats.notApplicable ?? 0
  const indeterminate = stats.indeterminate ?? 0
  const denyRate = total > 0 ? Math.round((deny / total) * 100) : 0

  return (
    <div className="my-2 rounded-lg border border-border dark:border-border/60 bg-card shadow-sm overflow-hidden">
      <div className="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
        <BarChart3 className="size-3.5 text-indigo-600 dark:text-indigo-400" />
        <span className="text-xs font-medium text-foreground">{data.message}</span>
      </div>

      {/* Summary row */}
      <div className="grid grid-cols-4 gap-0 divide-x divide-border/50">
        <StatCell
          icon={CheckCircle}
          label="Permit"
          value={permit}
          className="text-emerald-600 dark:text-emerald-400"
        />
        <StatCell
          icon={XCircle}
          label="Deny"
          value={deny}
          className="text-red-600 dark:text-red-400"
        />
        <StatCell
          icon={HelpCircle}
          label="N/A"
          value={notApplicable}
          className="text-gray-500 dark:text-gray-400"
        />
        <StatCell
          icon={AlertTriangle}
          label="Indet."
          value={indeterminate}
          className="text-amber-600 dark:text-amber-400"
        />
      </div>

      {/* Deny rate bar */}
      {total > 0 && (
        <div className="px-3.5 py-2 border-t border-border/50">
          <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-1">
            <span>Deny Rate</span>
            <span className={cn('font-medium', denyRate > 20 ? 'text-red-600 dark:text-red-400' : 'text-foreground')}>
              {denyRate}%
            </span>
          </div>
          <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
            <div
              className={cn(
                'h-full rounded-full transition-[background-color,width]',
                denyRate > 20 ? 'bg-red-500' : denyRate > 10 ? 'bg-amber-500' : 'bg-emerald-500'
              )}
              style={{ width: `${Math.min(denyRate, 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* Breakdown table */}
      {stats.breakdown && stats.breakdown.length > 0 && (
        <div className="border-t border-border/50">
          <div className="divide-y divide-border/40">
            {stats.breakdown.slice(0, 5).map((row) => (
              <div key={row.key} className="flex items-center justify-between px-3.5 py-1.5 text-[11px]">
                <span className="font-mono text-foreground truncate max-w-40">{row.key}</span>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-emerald-600 dark:text-emerald-400">{row.permit} permit</span>
                  <span className="text-red-600 dark:text-red-400">{row.deny} deny</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function StatCell({
  icon: Icon,
  label,
  value,
  className,
}: {
  icon: typeof CheckCircle
  label: string
  value: number
  className: string
}) {
  return (
    <div className="flex flex-col items-center gap-1 py-2.5 px-2">
      <Icon className={cn('size-3.5', className)} />
      <span className="text-lg font-semibold text-foreground">{value.toLocaleString()}</span>
      <span className="text-[10px] text-muted-foreground">{label}</span>
    </div>
  )
}
